/**
 * FoodPulse Nearby Deals — customer-facing GPS realtime deal feed.
 *
 * Route: /food-pulse/nearby-deals
 * Mission: reduce food waste by connecting shoppers with today's
 *          perishable deals happening within walking / driving range.
 *
 * Features
 *  - Uses browser Geolocation to fetch live position (or a saved sub).
 *  - Shows deals within radius, sorted by distance.
 *  - "Notify me when new deals drop near me" — creates a fp_geo_push_subs
 *    row + registers an FCM device token if not already registered.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Bell, BellOff, RefreshCw, Loader2, ShoppingBag, Tag } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";

function DealCard({ d }) {
  return (
    <Link to={`/food-pulse/deals/${d.slug}`} className="block rounded-2xl border bg-white p-4 hover:shadow-lg transition" style={{ borderColor: "#E9D8B4" }} data-testid={`deal-card-${d.slug}`}>
      <div className="flex items-start gap-3">
        {d.image_urls?.[0] && <img src={d.image_urls[0]} alt="" className="w-20 h-20 rounded-xl object-cover" />}
        <div className="flex-1">
          <div className="text-sm font-bold" style={{ color: GREEN }}>{d.title}</div>
          <div className="text-xs text-gray-600">{d.vendor_name || "Local store"}{d.city ? ` · ${d.city}` : ""}</div>
          <div className="flex items-center gap-2 mt-2">
            {d.discount_pct != null && <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: "#FDE68A", color: GREEN }}>−{d.discount_pct}%</span>}
            {d.deal_price != null && <span className="text-sm font-black" style={{ color: GREEN }}>${d.deal_price}{d.unit ? `/${d.unit}` : ""}</span>}
            {d.original_price != null && d.deal_price != null && <span className="text-xs text-gray-400 line-through">${d.original_price}</span>}
          </div>
        </div>
        <div className="text-xs font-bold flex flex-col items-end gap-1" style={{ color: GREEN }}>
          <div className="flex items-center gap-1"><MapPin size={12} />{d.distance_km} km</div>
        </div>
      </div>
    </Link>
  );
}

export default function FoodPulseNearbyDeals() {
  const { user } = useAuth();
  const [loc, setLoc] = useState(null);
  const [locError, setLocError] = useState("");
  const [radius, setRadius] = useState(5);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [subs, setSubs] = useState([]);
  const [subBusy, setSubBusy] = useState(false);

  const requestLocation = () => {
    setLocError("");
    if (!navigator.geolocation) { setLocError("Geolocation not supported"); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setLoc({ lat: p.coords.latitude, lon: p.coords.longitude }),
      (e) => setLocError(e.message || "Permission denied"),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const reload = useCallback(async () => {
    if (!loc) return;
    setLoading(true);
    try {
      const r = await http.get("/geo-push/nearby-deals", { params: { lat: loc.lat, lon: loc.lon, radius_km: radius } });
      setDeals(r.data.items || []);
    } catch (e) { console.warn(e); }
    setLoading(false);
  }, [loc, radius]);
  useEffect(() => { reload(); }, [reload]);

  // ---- Live SSE stream: new deals prepend at the top of the list ----
  const [liveCount, setLiveCount] = useState(0);
  useEffect(() => {
    if (!loc) return;
    const base = process.env.REACT_APP_BACKEND_URL;
    const url = `${base}/api/geo-push/stream?lat=${loc.lat}&lon=${loc.lon}&radius_km=${radius}`;
    const es = new EventSource(url, { withCredentials: true });
    es.addEventListener("deal", (evt) => {
      try {
        const deal = JSON.parse(evt.data);
        setDeals((prev) => {
          if (prev.some((d) => d.slug === deal.slug)) return prev;
          return [deal, ...prev];
        });
        setLiveCount((n) => n + 1);
      } catch { /* noop */ }
    });
    es.onerror = () => { /* browser auto-reconnects; leave silent */ };
    return () => es.close();
  }, [loc, radius]);

  const loadSubs = async () => {
    if (!user) return;
    try { const r = await http.get("/geo-push/subs"); setSubs(r.data.items || []); } catch { /* noop */ }
  };
  useEffect(() => { loadSubs(); /* eslint-disable-next-line */ }, [user]);

  const homeSub = subs.find((s) => s.label === "Home");
  const subscribed = !!homeSub && homeSub.active;

  const toggleSub = async () => {
    if (!user) { alert("Please sign in to subscribe."); return; }
    if (!loc) { requestLocation(); return; }
    setSubBusy(true);
    try {
      if (homeSub) {
        await http.post(`/geo-push/subs/${homeSub.id}/toggle`);
      } else {
        await http.post("/geo-push/subs", { label: "Home", lat: loc.lat, lon: loc.lon, radius_km: radius });
      }
      loadSubs();
    } catch (e) { alert(e.response?.data?.detail || "Sub failed"); }
    setSubBusy(false);
  };

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="nearby-deals-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <header className="mb-6">
          <h1 className="text-2xl font-black flex items-center gap-2" style={{ color: GREEN }}>
            <ShoppingBag /> Deals near you now
          </h1>
          <p className="text-sm text-gray-600 mt-1">Realtime perishable deals in your neighbourhood. Reduce waste, save money.</p>
        </header>

        {!loc ? (
          <div className="rounded-2xl border bg-white p-6 text-center" style={{ borderColor: "#E9D8B4" }} data-testid="geo-cta">
            <MapPin size={40} className="mx-auto mb-2" style={{ color: GREEN }} />
            <div className="font-semibold mb-2" style={{ color: GREEN }}>Share your location to see nearby deals</div>
            <div className="text-xs text-gray-600 mb-4">We use your position only to filter deals — never stored on our servers unless you save a subscription.</div>
            <button onClick={requestLocation} className="rounded-full px-5 py-2 font-semibold text-sm" style={{ background: GREEN, color: CREAM }} data-testid="allow-location-btn">Allow location</button>
            {locError && <div className="text-xs text-red-700 mt-3">{locError}</div>}
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <label className="text-sm text-gray-600 flex items-center gap-2">
                Radius
                <select value={radius} onChange={(e) => setRadius(Number(e.target.value))} className="rounded-full border px-3 py-1 text-sm" data-testid="radius-select">
                  {[1, 3, 5, 10, 20, 50].map((r) => <option key={r} value={r}>{r} km</option>)}
                </select>
              </label>
              <button onClick={reload} className="rounded-full border px-3 py-2 text-xs" style={{ borderColor: GREEN, color: GREEN }} data-testid="refresh-deals-btn">
                <RefreshCw size={12} className={`inline mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
              <button onClick={toggleSub} disabled={subBusy} className="rounded-full px-4 py-2 text-xs font-semibold flex items-center gap-1" style={{ background: subscribed ? "#E9D8B4" : GREEN, color: subscribed ? GREEN : CREAM }} data-testid="toggle-sub-btn">
                {subscribed ? <><BellOff size={12} /> Notifications on</> : <><Bell size={12} /> Notify me of new deals near here</>}
              </button>
              {liveCount > 0 && (
                <span className="text-xs font-bold px-2 py-1 rounded-full inline-flex items-center gap-1" style={{ background: "#BBF7D0", color: GREEN }} data-testid="live-badge">
                  <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: GREEN }} /> {liveCount} live now
                </span>
              )}
            </div>

            {loading && <div className="text-center p-6"><Loader2 className="animate-spin mx-auto" /></div>}
            {!loading && deals.length === 0 && (
              <div className="rounded-2xl border p-10 text-center bg-white" style={{ borderColor: "#E9D8B4" }} data-testid="no-deals-msg">
                <Tag className="mx-auto mb-2 text-gray-400" size={40} />
                <div className="text-gray-600">No deals within {radius} km right now. Try a larger radius or turn on notifications to be first to know.</div>
              </div>
            )}
            <div className="grid gap-3 md:grid-cols-2" data-testid="deals-grid">
              {deals.map((d) => <DealCard key={d.slug} d={d} />)}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
